import { useLiveQuery } from "dexie-react-hooks";
import { ArchiveRestore, ChevronRight, FolderClosed, Hash, Inbox, RotateCcw, SquareCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { colorHex, type ActivityEvent, type TrashItem } from "@todoer/shared";
import { Select } from "../components/overlay.js";
import { openTaskParams } from "../components/TaskList.js";
import { Avatar, Button, EmptyState, PageHeader, Spinner } from "../components/ui.js";
import { activityTarget, describeActivity } from "../lib/activity.js";
import { api } from "../lib/api.js";
import { db, useCollaborators, useUser } from "../lib/db.js";
import { relativeTime } from "../lib/format.js";
import { sortProjectTree } from "../lib/hooks.js";
import { cmd, dispatch, onRemoteChange, sync } from "../lib/sync.js";
import { showToast } from "../lib/toast.js";

const PAGE = 50;

export function Activity() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = params.get("project");
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const people = useCollaborators();
  const projects = useLiveQuery(() => db.projects.toArray(), [], []);
  const me = useUser();
  const sentinel = useRef<HTMLDivElement>(null);
  const hasIO = typeof IntersectionObserver !== "undefined";

  const load = useCallback(async (before?: number) => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: String(PAGE), ...(projectId ? { projectId } : {}), ...(before ? { before: String(before) } : {}) });
      const page = await api<ActivityEvent[]>(`/api/activity?${q}`);
      setEvents((prev) => (before ? [...prev, ...page] : page));
      setDone(page.length < PAGE);
      setError(null);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); setLoaded(true); }
  }, [projectId]);

  useEffect(() => { void load(); return onRemoteChange(() => void load()); }, [load]);

  const lastId = events[events.length - 1]?.id;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasIO || done || loading || !lastId) return;
    const io = new IntersectionObserver(([e]) => { if (e?.isIntersecting) void load(lastId); }, { root: document.getElementById("main-scroll"), rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasIO, done, loading, lastId, load]);

  const open = (e: ActivityEvent) => {
    const t = activityTarget(e);
    if (!t) return;
    if (t.kind === "task") setParams(openTaskParams(t.id));
    else navigate(projects.find((p) => p.id === t.id)?.isInbox ? "/inbox" : `/project/${t.id}`);
  };

  const options = [
    { value: null as string | null, label: "All projects" },
    ...sortProjectTree(projects).map((p) => ({ value: p.id as string | null, label: p.isInbox ? "Inbox" : p.name, indent: p.depth, icon: p.isInbox ? Inbox : Hash, iconColor: p.isInbox ? "var(--color-info)" : colorHex(p.color) })),
  ];

  const groups: { day: string; items: ActivityEvent[] }[] = [];
  for (const e of events) {
    const day = new Date(e.createdAt).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });
    const g = groups[groups.length - 1];
    if (g?.day === day) g.items.push(e); else groups.push({ day, items: [e] });
  }

  return (
    <div>
      <PageHeader title="Activity" subtitle="Everything that happened in your projects. Select a row to open it."
        actions={<div className="w-52 phone:w-40"><Select label="Filter by project" value={projectId} options={options} search={options.length > 8}
          onChange={(id) => setParams((p) => { if (id) p.set("project", id); else p.delete("project"); return p; })} /></div>} />
      {error && <p className="mb-4 rounded-card bg-danger-soft px-3 py-2 text-body text-danger">{error}. Activity needs a connection.</p>}
      {groups.map((g) => (
        <section key={g.day} className="mb-6" aria-label={g.day}>
          <h2 className="sticky top-0 z-10 border-b border-border bg-surface/90 py-2 text-caption font-semibold uppercase text-text-faint backdrop-blur-xl phone:top-[calc(44px+env(safe-area-inset-top))]">{g.day}</h2>
          <ul>
            {g.items.map((e) => {
              const d = describeActivity(e, people, projects, me?.id);
              const p = projects.find((x) => x.id === e.projectId);
              const target = activityTarget(e);
              return (
                <li key={e.id}>
                  <button type="button" onClick={() => open(e)} disabled={!target}
                    className="group flex w-full items-center gap-3 rounded-control px-2 py-2.5 text-left transition-colors hover:bg-hover phone:min-h-14">
                    <Avatar person={people.find((x) => x.id === e.userId) ?? { name: d.who, image: null }} size={28} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-body"><span className="font-semibold">{d.who}</span> {d.text}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-meta text-text-faint">
                        {d.where && (
                          <span className="inline-flex max-w-48 items-center gap-1.5 rounded-full bg-surface-sunken px-2 py-0.5 text-text-muted">
                            <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: p?.isInbox ? "var(--color-info)" : colorHex(p?.color ?? "grey") }} />
                            <span className="truncate">{d.where.replace(/^#/, "")}</span>
                          </span>
                        )}
                        <time dateTime={e.createdAt} title={new Date(e.createdAt).toLocaleString()} className="tabular">{relativeTime(e.createdAt)}</time>
                      </span>
                    </span>
                    {target && <ChevronRight size={16} aria-hidden className="shrink-0 text-text-faint transition-transform group-hover:translate-x-0.5 group-hover:text-text" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {loaded && !events.length && !error && <EmptyState kind="activity" title="No activity yet">Changes you and your teammates make show up here.</EmptyState>}
      {!done && events.length > 0 && (hasIO
        ? <div ref={sentinel} className="flex h-12 items-center justify-center">{loading && <Spinner />}</div>
        : <Button className="mt-2" loading={loading} onClick={() => void load(lastId)}>Load more</Button>)}
    </div>
  );
}

export function Trash() {
  const [items, setItems] = useState<TrashItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const projects = useLiveQuery(() => db.projects.toArray(), [], []);
  const load = useCallback(() => api<TrashItem[]>("/api/trash").then((x) => { setItems(x); setError(null); }).catch((e: Error) => setError(e.message)), []);
  useEffect(() => { void load(); return onRemoteChange(() => void load()); }, [load]);

  const restore = async (it: TrashItem) => {
    await dispatch(cmd(it.type === "project" ? "project_restore" : "item_restore", { id: it.id }));
    await sync();
    showToast(`Restored "${it.content}"`);
    void load();
  };

  return (
    <div>
      <PageHeader title="Trash" subtitle="Deleted tasks and projects are kept for 30 days." />
      {error && <p className="mb-4 rounded-card bg-danger-soft px-3 py-2 text-body text-danger">{error}. Trash needs a connection.</p>}
      {items && !items.length && <EmptyState kind="trash" title="Trash is empty">Deleted tasks and projects wait here for 30 days.</EmptyState>}
      {!items && !error && <div className="flex justify-center py-10"><Spinner /></div>}
      <ul>
        {items?.map((it) => {
          const p = projects.find((x) => x.id === it.projectId);
          const Icon = it.type === "project" ? FolderClosed : SquareCheck;
          return (
            <li key={`${it.type}-${it.id}`} className="flex items-center gap-3 border-b border-border py-2.5 phone:min-h-14">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-control bg-surface-sunken text-text-muted"><Icon size={16} aria-hidden /></span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-body">{it.content}</div>
                <div className="truncate text-meta text-text-faint">
                  {it.type === "project" ? "Project" : p ? (p.isInbox ? "Inbox" : p.name) : "Task"} · deleted {relativeTime(it.deletedAt)}
                </div>
              </div>
              <Button size="sm" icon={RotateCcw} className="phone:h-11" onClick={() => void restore(it)}>Restore</Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function Archived() {
  const projects = useLiveQuery(() => db.projects.filter((p) => p.isArchived).toArray(), [], undefined);
  const navigate = useNavigate();
  return (
    <div>
      <PageHeader title="Archived projects" subtitle="Projects tidied away without deleting them." />
      {projects && !projects.length && <EmptyState kind="archive" title="No archived projects">Archive a project from its ⋯ menu to tidy it away without deleting it.</EmptyState>}
      <ul>
        {projects?.map((p) => (
          <li key={p.id} className="flex items-center gap-3 border-b border-border py-2.5 phone:min-h-14">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-control bg-surface-sunken"><Hash size={16} aria-hidden style={{ color: colorHex(p.color) }} /></span>
            <button type="button" className="min-w-0 flex-1 text-left" onClick={() => navigate(`/project/${p.id}`)}>
              <span className="block truncate text-body hover:underline">{p.name}</span>
              <span className="block text-meta text-text-faint">Archived {relativeTime(p.updatedAt)}</span>
            </button>
            <Button size="sm" icon={ArchiveRestore} className="phone:h-11"
              onClick={() => { void dispatch(cmd("project_update", { id: p.id, isArchived: false })); showToast(`Unarchived "${p.name}"`); }}>Unarchive</Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
